import { useState, useEffect } from "react";
import { Table, Input, Tag, notification, Drawer, Spin, DatePicker } from "antd";
import dayjs from "dayjs";
import { Button, Icon } from "~/components";
import { saleApi } from "~/api";
import { handleRequest, apiError, renderDateTime, strToTime } from "~/utils";
import SaleCallSourceEventForm from "../Forms/SaleCallSourceEventForm";

// Phải chờ 15 phút giữa 2 lần gọi cùng 1 khách
const CALL_COOLDOWN = 15 * 60;

const CALL_STATUS = {
	0: { color: 'default', text: (_) => 'Chưa gọi' },
	1: { color: 'green',   text: (count) => count > 0 ? `Đã gọi lần ${count}` : 'Đã gọi' },
	2: { color: 'blue',    text: (_)     => 'Đã hẹn' },
};

// Gợi ý kết quả cuộc gọi để nhân viên chọn nhanh
const QUICK_RESULTS = [
	'Khách không nghe máy',
	'Chưa có nhu cầu',
	'Thuê bao',
	'Cúp máy ngang',
	'Khách đang bận',
	'Yêu cầu gửi thông tin qua zalo',
	'Hỏi giá',
];

function SaleCallSourceContactTable({
	contacts,
	pagination,
	loading,
	onPaginationChange,
	onReload,
	onPatch,
	rowSelection,
	showOwner,
}) {

	const [contactEdit, setContactEdit]       = useState(null);
	const [callNote, setCallNote]             = useState('');
	const [callHistory, setCallHistory]       = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [activePanel, setActivePanel]       = useState(null);
	const [confirming, setConfirming]         = useState(false);

	// Đồng hồ để hạ trạng thái "đang chờ gọi lại" khi hết 15 phút
	const [now, setNow] = useState(Math.floor(Date.now() / 1000));
	useEffect(() => {
		const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 30000);
		return () => clearInterval(t);
	}, []);

	// inline editing
	const [editingNoteId, setEditingNoteId]       = useState(null);
	const [editingNoteValue, setEditingNoteValue] = useState('');
	const [editingWebId, setEditingWebId]         = useState(null);
	const [editingWebValue, setEditingWebValue]   = useState('');

	const cooldownRemain = (record) => {
		const last = Number(record.callAt) || 0;
		if (last <= 0) return 0;
		const remain = CALL_COOLDOWN - (now - last);
		return remain > 0 ? Math.ceil(remain / 60) : 0;
	};

	const openCallPanel = async (record) => {
		setContactEdit(record);
		setCallNote('');
		setActivePanel('call');
		if (record.callCount > 0) {
			setHistoryLoading(true);
			let [, response] = await handleRequest(saleApi.callSourceContactHistory(record.id));
			setCallHistory(response?.data || []);
			setHistoryLoading(false);
		} else {
			setCallHistory([]);
		}
	};

	const openEventPanel = (record) => {
		setContactEdit(record);
		setActivePanel('event');
	};

	const closePanel = () => {
		setActivePanel(null);
		setCallNote('');
		setCallHistory([]);
	};

	// Inline note
	const startNoteEdit = (record) => {
		setEditingNoteId(record.id);
		setEditingNoteValue(record.note || '');
	};
	const saveNote = async (id) => {
		let [error, response] = await handleRequest(
			saleApi.callSourceContactUpdate(id, { note: editingNoteValue })
		);
		let message = apiError('Cập nhật ghi chú thất bại', error, response);
		if (!message) onPatch?.(id, { note: editingNoteValue });
		setEditingNoteId(null);
	};

	// Inline website
	const startWebEdit = (record) => {
		setEditingWebId(record.id);
		setEditingWebValue(record.website || '');
	};
	const saveWebsite = async (id) => {
		let [error, response] = await handleRequest(
			saleApi.callSourceContactUpdate(id, { website: editingWebValue })
		);
		let message = apiError('Cập nhật website thất bại', error, response);
		if (!message) onPatch?.(id, { website: editingWebValue });
		setEditingWebId(null);
	};

	// Inline ngày hết hạn website
	const saveWebsiteExpired = async (id, value) => {
		const formatted = value ? value.format('YYYY-MM-DD') : '';
		let [error, response] = await handleRequest(
			saleApi.callSourceContactUpdate(id, { websiteExpired: formatted })
		);
		let message = apiError('Cập nhật ngày hết hạn thất bại', error, response);
		if (!message) onPatch?.(id, { websiteExpired: formatted || null });
	};

	// Xác nhận đã gọi (bắt buộc nhập kết quả)
	const handleConfirmCall = async () => {
		if (!contactEdit?.id) return;
		if (!callNote.trim()) {
			notification.warning({ message: 'Thiếu kết quả', description: 'Vui lòng nhập kết quả cuộc gọi' });
			return;
		}
		setConfirming(true);
		let [error, response] = await handleRequest(
			saleApi.callSourceContactCalled(contactEdit.id, { note: callNote })
		);
		setConfirming(false);
		let message = apiError('Xác nhận gọi điện thất bại', error, response);
		if (!message) {
			notification.success({ message: 'Thành công', description: 'Đã xác nhận gọi điện' });
			closePanel();
			onReload?.();
		}
	};

	// Tạo cuộc hẹn
	const handleCreateEvent = async (data, contact) => {
		data.day = strToTime(data.day);
		let [error, response] = await handleRequest(saleApi.eventAdd(data));
		let message = apiError('Tạo lịch hẹn thất bại', error, response);
		if (!message) {
			if (!response?.data?.id) {
				notification.error({ message: 'Thất bại', description: 'Chưa tạo được lịch hẹn' });
			} else {
				notification.success({ message: 'Thành công', description: `Đã tạo lịch hẹn cho ${contact?.name}` });
				await handleRequest(saleApi.callSourceContactSetAppointment(contact.id));
				closePanel();
				onReload?.();
			}
		}
	};

	const columns = [
		{
			title: 'STT',
			key: 'stt',
			width: 50,
			render: (_, __, i) => (pagination.page - 1) * pagination.limit + i + 1,
		},
		{ title: 'Tên khách/công ty', dataIndex: 'name',  key: 'name',  width: 210 },
		{ title: 'Số điện thoại',     dataIndex: 'phone', key: 'phone', width: 120 },
		{ title: 'Địa chỉ',           dataIndex: 'address', key: 'address', width: 200 },
		...(showOwner ? [{
			title: 'Phụ trách',
			key: 'owner',
			width: 150,
			render: (_, record) => record.user
				? <span>{`${record.user.firstname || ''} ${record.user.lastname || ''}`.trim()}</span>
				: <span style={{ color: '#bbb' }}>—</span>,
		}] : []),
		{
			title: 'Ghi chú',
			key: 'note',
			width: 180,
			render: (_, record) => {
				if (editingNoteId === record.id) {
					return (
						<div onClick={e => e.stopPropagation()}>
							<Input.TextArea
								autoSize={{ minRows: 2, maxRows: 4 }}
								value={editingNoteValue}
								onChange={e => setEditingNoteValue(e.target.value)}
								onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); saveNote(record.id); } }}
								autoFocus
								style={{ marginBottom: 4 }}
							/>
							<div className="d-flex gap">
								<Button small primary onClick={() => saveNote(record.id)}>Lưu</Button>
								<Button small white onClick={() => setEditingNoteId(null)}>Hủy</Button>
							</div>
						</div>
					);
				}
				return (
					<div style={{ cursor: 'pointer', minHeight: 22 }} onClick={() => startNoteEdit(record)} title="Click để sửa">
						{record.note
							? <span>{record.note}</span>
							: <span style={{ color: '#bbb', fontStyle: 'italic' }}>+ Thêm ghi chú</span>
						}
					</div>
				);
			},
		},
		{
			title: 'Website',
			key: 'website',
			width: 170,
			render: (_, record) => {
				if (editingWebId === record.id) {
					return (
						<div onClick={e => e.stopPropagation()}>
							<Input
								value={editingWebValue}
								onChange={e => setEditingWebValue(e.target.value)}
								onPressEnter={() => saveWebsite(record.id)}
								placeholder="https://..."
								autoFocus
								style={{ marginBottom: 4 }}
							/>
							<div className="d-flex gap">
								<Button small primary onClick={() => saveWebsite(record.id)}>Lưu</Button>
								<Button small white onClick={() => setEditingWebId(null)}>Hủy</Button>
							</div>
						</div>
					);
				}
				return (
					<div style={{ cursor: 'pointer', minHeight: 22 }} onClick={() => startWebEdit(record)} title="Click để sửa">
						{record.website
							? <a href={record.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#52c41a' }}>{record.website}</a>
							: <span style={{ color: '#bbb', fontStyle: 'italic' }}>+ Thêm website</span>
						}
					</div>
				);
			},
		},
		{
			title: 'Ngày hết hạn web',
			key: 'websiteExpired',
			width: 150,
			render: (_, record) => {
				const value = record.websiteExpired ? dayjs(record.websiteExpired) : null;
				const expired = value && value.isBefore(dayjs(), 'day');
				return (
					<DatePicker
						value={value}
						size="small"
						allowClear
						format="DD/MM/YYYY"
						placeholder="Chọn ngày"
						style={{ width: '100%' }}
						status={expired ? 'error' : ''}
						onChange={(d) => saveWebsiteExpired(record.id, d)}
					/>
				);
			},
		},
		{
			title: 'Trạng thái',
			key: 'status',
			width: 120,
			render: (_, record) => {
				const s   = CALL_STATUS[record.status] || CALL_STATUS[0];
				const txt = s.text(record.callCount ?? 0);
				return <Tag color={s.color}>{txt}</Tag>;
			},
		},
		{
			title: '',
			key: 'action',
			width: 115,
			fixed: 'right',
			render: (_, record) => {
				const remain = cooldownRemain(record);
				return (
					<div className="d-flex gap">
						<Button
							small outline
							leftIcon={Icon.headphones}
							disabled={remain > 0}
							style={{ color: remain > 0 ? '#bbb' : record.status == 2 ? '#1677ff' : record.status == 1 ? '#52c41a' : undefined }}
							onClick={() => openCallPanel(record)}
							title={remain > 0 ? `Vừa gọi, chờ ${remain} phút` : 'Xác nhận gọi điện'}
						/>
						<Button
							small blue outline
							leftIcon={Icon.calendar}
							onClick={() => openEventPanel(record)}
							title="Tạo cuộc hẹn"
						/>
					</div>
				);
			},
		},
	];

	return (
		<div>
			<Table
				columns={columns}
				dataSource={contacts}
				rowKey="id"
				size="small"
				loading={loading}
				rowSelection={rowSelection}
				scroll={{ x: 1280 }}
				rowClassName={(record) => {
					if (record.status == 2) return 'ant-table-row-appointed';
					if (record.status == 1) return 'ant-table-row-called';
					return '';
				}}
				pagination={{
					current: pagination.page,
					pageSize: pagination.limit,
					total: pagination.total,
					onChange: (page) => onPaginationChange?.(page),
					showTotal: (t) => `Tổng ${t} liên hệ`,
				}}
			/>

			{/* Drawer: Xác nhận gọi điện */}
			<Drawer
				title="Xác nhận gọi điện"
				open={activePanel === 'call'}
				onClose={closePanel}
				width={500}
				destroyOnClose
			>
				{contactEdit && (
					<div>
						<div style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
							<p style={{ margin: '3px 0' }}><b>Tên:</b> {contactEdit.name}</p>
							<p style={{ margin: '3px 0' }}><b>Số điện thoại:</b> {contactEdit.phone}</p>
							{contactEdit.address && (
								<p style={{ margin: '3px 0' }}><b>Địa chỉ:</b> {contactEdit.address}</p>
							)}
						</div>
						{contactEdit.callCount > 0 && (
							<div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, padding: '6px 12px', marginBottom: 12 }}>
								<span style={{ color: '#fa8c16', fontSize: 13 }}>
									Đã gọi {contactEdit.callCount} lần. Xác nhận sẽ ghi thêm lịch sử.
								</span>
							</div>
						)}
						<div className="form-group" style={{ marginBottom: 16 }}>
							<label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>
								Kết quả cuộc gọi <span style={{ color: '#f5222d' }}>*</span>
							</label>
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
								{QUICK_RESULTS.map((result) => (
									<Tag.CheckableTag
										key={result}
										checked={callNote === result}
										onChange={(checked) => setCallNote(checked ? result : '')}
										style={{ border: '1px solid #d9d9d9', cursor: 'pointer', padding: '2px 10px', margin: 0 }}
									>
										{result}
									</Tag.CheckableTag>
								))}
							</div>
							<textarea
								style={{
									width: '100%', padding: '8px 10px',
									border: '1px solid #d9d9d9', borderRadius: 4,
									fontSize: 13, resize: 'vertical', minHeight: 110,
									boxSizing: 'border-box',
								}}
								value={callNote}
								onChange={e => setCallNote(e.target.value)}
								placeholder="Bắt buộc nhập kết quả cuộc gọi..."
							/>
						</div>
						<div className="d-flex justify-content-end gap" style={{ marginBottom: 20 }}>
							<Button white leftIcon={Icon.close} onClick={closePanel}>Đóng</Button>
							<Button
								primary
								leftIcon={confirming ? Icon.loading : Icon.success}
								disabled={confirming || !callNote.trim()}
								onClick={handleConfirmCall}
							>
								Xác nhận đã gọi
							</Button>
						</div>

						{(historyLoading || callHistory.length > 0) && (
							<div>
								<div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#555' }}>Lịch sử cuộc gọi</div>
								{historyLoading ? (
									<div style={{ textAlign: 'center', padding: 12 }}><Spin size="small" /></div>
								) : (
									<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
										{callHistory.map((h, idx) => (
											<div key={h.id} style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 4, padding: '8px 12px', fontSize: 12 }}>
												<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
													<span style={{ color: '#888' }}>Lần {callHistory.length - idx}</span>
													<span style={{ color: '#888' }}>{renderDateTime(h.time)}</span>
												</div>
												{h.note
													? <div style={{ color: '#333' }}>{h.note}</div>
													: <div style={{ color: '#bbb', fontStyle: 'italic' }}>Không có ghi chú</div>
												}
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</div>
				)}
			</Drawer>

			{/* Drawer: Tạo cuộc hẹn */}
			<Drawer
				title={`Tạo cuộc hẹn — ${contactEdit?.name || ''}`}
				open={activePanel === 'event'}
				onClose={closePanel}
				width={600}
				destroyOnClose
			>
				<SaleCallSourceEventForm
					contact={contactEdit}
					onHandleSubmit={handleCreateEvent}
				/>
			</Drawer>
		</div>
	);
}

export default SaleCallSourceContactTable;
